import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Download, LogOut, RefreshCw, Users, ShieldCheck } from "lucide-react";
import { adminExportCsv } from "../lib/api";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { AdminLogin } from "../components/admin/AdminLogin";
import { SignupsTable } from "../components/admin/SignupsTable";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

export default function Admin() {
  const { token, authed, checking, items, count, loading, login, logout, refresh } = useAdminAuth();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await adminExportCsv(token);
      toast.success("CSV-Export gestartet.");
    } catch {
      toast.error("Export fehlgeschlagen.");
    } finally {
      setExporting(false);
    }
  };

  if (checking) {
    return (
      <div className="grid min-h-screen place-items-center bg-[var(--bg)]">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--blue)]" data-testid="admin-checking-spinner" />
      </div>
    );
  }

  if (!authed) {
    return <AdminLogin onLogin={login} />;
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      {/* Top bar */}
      <header className="sticky top-0 z-10 border-b border-[var(--border)] bg-white/85 backdrop-blur">
        <div className="mx-auto flex max-w-[1120px] items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-heading text-lg font-bold text-[var(--ink)]">
              Procur<span className="text-[var(--blue)]">mo</span>
            </Link>
            <span className="hidden h-4 w-px bg-[var(--border)] sm:block" />
            <span className="hidden text-sm font-medium text-[var(--slate)] sm:block">Admin · Warteliste</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={loading}
              data-testid="admin-refresh-button"
              className="gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="admin-logout-button"
              className="gap-1.5 text-[var(--slate)]"
            >
              <LogOut className="h-3.5 w-3.5" /> Abmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1120px] px-4 py-8 sm:px-6">
        {/* Summary + actions */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-[var(--ink)]">Anmeldungen</h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-[var(--slate)]">
              <Users className="h-4 w-4 text-[var(--blue)]" />
              Insgesamt
              <Badge className="bg-[var(--blue-soft)] text-[var(--blue)] hover:bg-[var(--blue-soft)]" data-testid="admin-total-count">
                {count}
              </Badge>
            </div>
          </div>
          <Button
            onClick={handleExport}
            disabled={exporting || count === 0}
            data-testid="admin-export-button"
            className="gap-2 bg-[var(--blue)] text-white hover:bg-[var(--blue-2)]"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            CSV exportieren
          </Button>
        </div>

        <SignupsTable items={items} loading={loading} />

        <p className="mt-4 flex items-center gap-1.5 text-xs text-[var(--slate)]">
          <ShieldCheck className="h-3.5 w-3.5 text-[var(--green)]" />
          Daten liegen sicher in der eigenen Datenbank. Zugriff nur mit Admin-Passwort.
        </p>
      </main>
    </div>
  );
}
